import { useCallback, useEffect, useRef, useState } from "react";
import avatar from "@/assets/varvara-portrait.webp";
import { OFFER_URL, PRIVACY_URL } from "@/lib/site";
import { getOrCreateClickToken } from "@/lib/track";
import "@/pages/chat.css";

const runtimeConfig = (typeof window !== "undefined" && window.APP_CONFIG) || {};
const API_BASE = runtimeConfig.API_BASE || "";
const WEB_ID_KEY = "tv_web_id";
const POLL_MS = 2000;

function readWebId() {
  try {
    const id = window.localStorage.getItem(WEB_ID_KEY);
    return /^[0-9a-f]{32}$/.test(id || "") ? id : null;
  } catch {
    return null;
  }
}

function saveWebId(id) {
  try {
    window.localStorage.setItem(WEB_ID_KEY, id);
  } catch {
    // приватный режим — работаем в рамках одной вкладки
  }
}

function startPayload() {
  const params = new URLSearchParams(window.location.search);
  return params.get("start") || getOrCreateClickToken() || undefined;
}

// Бот отвечает markdown-разметкой (та же, что уходит в Telegram и MAX) — здесь нужен
// только минимум: жирный, курсив, зачёркнутый. HTML из ответа не рендерим.
function renderMarkdown(text) {
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return escaped
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/~~([^~]+)~~/g, "<del>$1</del>")
    .replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
}

async function api(path, options) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  if (!res.ok) throw new Error(`${path} ${res.status}`);
  return res.json();
}

export default function Chat() {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [waiting, setWaiting] = useState(true);
  const [error, setError] = useState(null);
  const webIdRef = useRef(null);
  const cursorRef = useRef(0);
  const pollingRef = useRef(false);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  // Одновременно идут два опроса — по таймеру и сразу после отправки. Без флага
  // оба уходят с одним и тем же курсором и вставляют историю дважды.
  const poll = useCallback(async () => {
    const webId = webIdRef.current;
    if (!webId || pollingRef.current) return;
    pollingRef.current = true;
    try {
      const { messages: incoming } = await api("/api/web/poll", {
        method: "POST",
        body: JSON.stringify({ webId, after: cursorRef.current }),
      });
      if (!incoming?.length) return;
      cursorRef.current = Math.max(cursorRef.current, incoming[incoming.length - 1].seq);
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.seq));
        const fresh = incoming.filter((m) => !seen.has(m.seq)).map((m) => ({ ...m, from: "bot" }));
        return fresh.length ? [...prev, ...fresh] : prev;
      });
      setWaiting(false);
    } catch (e) {
      setError("Связь с ботом прервалась. Обновите страницу.");
    } finally {
      pollingRef.current = false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const existing = readWebId();
        const session = await api("/api/web/session", {
          method: "POST",
          body: JSON.stringify({ webId: existing || undefined, startPayload: existing ? undefined : startPayload() }),
        });
        if (cancelled) return;
        webIdRef.current = session.webId;
        saveWebId(session.webId);
        await poll();
      } catch (e) {
        if (!cancelled) setError("Не удалось открыть чат. Попробуйте обновить страницу.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [poll]);

  useEffect(() => {
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, [poll]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, waiting]);

  const say = useCallback(async (payload, echo) => {
    const webId = webIdRef.current;
    if (!webId) return;
    if (echo) setMessages((prev) => [...prev, { seq: `local_${Date.now()}`, from: "user", text: echo }]);
    setWaiting(true);
    setError(null);
    try {
      await api("/api/web/say", { method: "POST", body: JSON.stringify({ webId, ...payload }) });
      await poll();
    } catch (e) {
      setWaiting(false);
      setError("Сообщение не отправилось. Попробуйте ещё раз.");
    }
  }, [poll]);

  const sendText = () => {
    const value = draft.trim();
    if (!value) return;
    setDraft("");
    void say({ type: "text", text: value }, value);
  };

  const sendPhoto = async (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      setError("Фото больше 6 МБ — сделайте снимок поменьше.");
      return;
    }
    const data = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
      reader.readAsDataURL(file);
    });
    void say({ type: "photo", photo: { data, mimeType: file.type || "image/jpeg" } }, "📷 Фото отправлено");
  };

  const lastButtons = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.from !== "bot") continue;
      return m.buttons?.length ? m.buttons : [];
    }
    return [];
  })();

  return (
    <div className="chat-page">
      <header className="chat-header">
        <img className="chat-avatar" src={avatar} alt="Бабушка Варвара" />
        <div>
          <div className="chat-title">Бабушка Варвара</div>
          <div className="chat-status">в сети</div>
        </div>
        <a className="chat-home" href="/">
          На сайт
        </a>
      </header>

      <div className="chat-body">
        {messages.map((m) => (
          <div key={m.seq} className={`chat-msg chat-msg--${m.from}`}>
            <span dangerouslySetInnerHTML={{ __html: renderMarkdown(m.text) }} />
            {m.imageUrl && <img src={m.imageUrl} alt="Иллюстрация к разбору" loading="lazy" />}
            {m.audioUrl && <audio controls src={m.audioUrl} />}
          </div>
        ))}

        {waiting && (
          <div className="chat-typing" data-testid="chat-typing">
            <span />
            <span />
            <span />
          </div>
        )}

        {error && <div className="chat-error">{error}</div>}

        {!waiting && lastButtons.length > 0 && (
          <div className="chat-buttons">
            {lastButtons.flat().map((b, i) =>
              b.url ? (
                <a
                  key={`${b.text}_${i}`}
                  className="chat-btn chat-btn--pay"
                  href={b.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {b.text}
                </a>
              ) : (
                <button
                  key={`${b.text}_${i}`}
                  className="chat-btn"
                  type="button"
                  onClick={() => say({ type: "callback", action: b.action }, b.text)}
                >
                  {b.text}
                </button>
              ),
            )}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      <div className="chat-composer">
        <div className="chat-composer-inner">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              void sendPhoto(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <button className="chat-attach" type="button" title="Отправить фото ладони" onClick={() => fileRef.current?.click()}>
            📎
          </button>
          <textarea
            className="chat-input"
            rows={1}
            placeholder="Написать сообщение…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendText();
              }
            }}
          />
          <button className="chat-send" type="button" onClick={sendText} disabled={!draft.trim()}>
            ➤
          </button>
        </div>
        <p className="chat-note">
          Развлекательный формат, не является консультацией. Оплата — ЮKassa.{" "}
          <a href={OFFER_URL}>Оферта</a> · <a href={PRIVACY_URL}>Политика</a>
        </p>
      </div>
    </div>
  );
}
